import { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";

const MESSAGES = [
  "Reading your recipe…",
  "Extracting ingredients…",
  "Structuring steps…",
  "Cleaning it up…",
  "Plating it nicely…",
];

interface Props {
  /** Optional override message (e.g. "Grabbing a screenshot…") */
  message?: string;
  subMessage?: string;
  /**
   * Upload progress 0-1. -1 means indeterminate (request in flight, total
   * unknown). undefined hides the bar entirely.
   */
  progress?: number;
}

export const LoadingState = ({ message, subMessage, progress }: Props) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (message) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % MESSAGES.length);
    }, 1800);
    return () => clearInterval(t);
  }, [message]);

  const showProgress = typeof progress === "number";
  const indeterminate = showProgress && (progress! < 0 || progress! >= 1);
  const pct = showProgress
    ? Math.max(0, Math.min(100, Math.round((progress ?? 0) * 100)))
    : 0;

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-8 px-6 text-center">
      <div className="relative h-16 w-16">
        <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
        <div className="absolute inset-2 animate-pulse rounded-full bg-primary/40" />
        <div className="absolute inset-5 rounded-full bg-primary" />
      </div>
      <div className="min-h-[2.25rem] py-1">
        <p
          key={message ?? index}
          className="animate-fade-up font-display text-xl leading-[1.4] text-foreground/80"
        >
          {message ?? MESSAGES[index]}
        </p>
      </div>
      {showProgress && (
        <div className="w-full max-w-xs space-y-2">
          {indeterminate ? (
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div className="absolute inset-y-0 w-1/3 animate-[indeterminate_1.4s_ease-in-out_infinite] rounded-full bg-primary" />
            </div>
          ) : (
            <Progress value={pct} className="h-2" />
          )}
          <p className="text-xs text-muted-foreground tabular-nums">
            {indeterminate
              ? progress! >= 1
                ? "Finishing up…"
                : "Sending…"
              : `${pct}%`}
          </p>
        </div>
      )}
      <p className="text-sm text-muted-foreground">
        {subMessage ?? "This usually takes 5–15 seconds"}
      </p>
    </div>
  );
};
